import React, { memo, useMemo } from 'react';
import { useYbrCrossFlowSuppression } from '@jira/yellow-brick-road-utils/src/controllers/useYbrCrossFlowSuppression.tsx';
import { MenuList, MenuListItem, MenuSection } from '@atlaskit/navigation-system';
import { useIntl } from '@atlassian/jira-intl';
import { useProjectsAnalyticsL1 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l1/index.tsx';
import { MENU_ID_PROJECTS_VIEW_ALL } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { ViewAllEntityMenuItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/view-all-entity-menu-item/index.tsx';
import { projectsDirectoryRoute } from '@atlassian/jira-router-routes-directories-routes/src/projectsDirectoryRoute.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { MoreProjectsFlyout } from '@atlassian/jira-navigation-apps-sidebar-nav4-more-projects/src/ui/flyout/more-projects-flyout.tsx';
import { BrowseTemplatesMenuItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-projects-browse-templates/src/ui/index.tsx';
import { Nav4ProjectsSectionPlacement } from '@atlassian/jira-growth-nav-4-projects-section/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { Nav4Recommendations } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-growth-recommendations/src/index.tsx';
import messages from './messages.tsx';

const VIEW_ALL_PROJECTS_HREF = projectsDirectoryRoute.path;

function FooterInternal({
	hasMore = false,
	canCreateProject = false,
}: {
	hasMore?: boolean | null;
	canCreateProject?: boolean | null;
}): React.JSX.Element {
	const moreProjectsGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.moreProjectsGalaxia
		: messages.moreProjects;
	const viewAllProjectsGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.viewAllProjectsGalaxia
		: messages.viewAllProjects;
	const { formatMessage } = useIntl();
	const { sendViewAllProjectsAnalyticsEvent, sendMoreProjectsAnalyticsEvent } =
		useProjectsAnalyticsL1();
	const { shouldSuppress } = useYbrCrossFlowSuppression();

	const recommendations = useMemo(() => {
		if (
			!canCreateProject ||
			!fg('sqa-1995_move_nav4recommendations_into_footer') ||
			shouldSuppress()
		) {
			return null;
		}
		return expVal('grw-1672_display_targeted_jsm_template_v2', 'isEnabled', false) ? (
			<Nav4ProjectsSectionPlacement />
		) : (
			<Nav4Recommendations />
		);
	}, [canCreateProject, shouldSuppress]);

	return (
		<MenuListItem>
			<MenuSection>
				<MenuList>
					{expVal('blu-7465-projects-search-in-sidebar', 'isEnabled', false) || hasMore ? (
						<MoreProjectsFlyout onClick={sendMoreProjectsAnalyticsEvent}>
							{formatMessage(moreProjectsGalaxia)}
						</MoreProjectsFlyout>
					) : (
						<ViewAllEntityMenuItem
							onClick={sendViewAllProjectsAnalyticsEvent}
							href={VIEW_ALL_PROJECTS_HREF}
							menuId={MENU_ID_PROJECTS_VIEW_ALL}
						>
							{formatMessage(viewAllProjectsGalaxia)}
						</ViewAllEntityMenuItem>
					)}
					{recommendations}
					{canCreateProject &&
						expVal('jsmgrowth-216_template_gallery_in_left_navigation', 'isEnabled', false) && (
							<BrowseTemplatesMenuItem />
						)}
				</MenuList>
			</MenuSection>
		</MenuListItem>
	);
}

export const Footer = memo(FooterInternal);
